import React, { useContext, useMemo } from 'react'
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  CardHeader,
  Typography,
  Grid,
  Box,
  Tooltip,
} from "@mui/material";
import EyeBrow from 'component/MarketPlace/eyeBrow/eyeBrow';
import HealthyChip from 'component/commonComponents/HealthyChip';
import { StyledIcon } from 'component/commonComponents/Icon/icon.style';
import { ThemeContext } from 'styled-components';
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { DEVICE_STATUS, NOT_APPLICABLE } from '../../constants/originalState';
import { StyledButton } from './deviceDetail.style';
import { PerformanceConfig } from 'models/Device';
import GenericDeviceImage from "../../assets/images/generic_laptop.png";
import { mapTextId } from 'utils/mapTextId';

export interface IManageDeviceDetailProps{
  device: any;
  performance: string;
  locale: any;
  pcManagement: any;
  onClickAction: (item: any) => void;
  performanceConfig: PerformanceConfig;
  ownedBy?: string;
  isShowPrimaryLabel?: boolean;
  isShowOwnedBy?: boolean;
  localePerformance?: any;
};




const DeviceDetail = ({ device, performance , locale, pcManagement, onClickAction, performanceConfig, ownedBy, isShowOwnedBy = true, isShowPrimaryLabel, localePerformance} : IManageDeviceDetailProps) => {
  const theme = useContext(ThemeContext);
  const isOrange = [DEVICE_STATUS.INACTIVE].includes(!!device.process_stage.asset_eyebrow && device.process_stage.asset_eyebrow?.toUpperCase());
  const assetEyebrow = device?.process_stage?.asset_eyebrow?.toUpperCase() || '';
  const status = device?.process_stage?.stage?.toUpperCase();
  const assetStatus = device?.asset_function?.toUpperCase();

  const textAssetFunction = locale?.commonStatus?.[assetStatus!]
    ? ` - ${locale?.commonStatus[assetStatus!]}`
    : '';
  
  const isInactiveStatus = useMemo(() => {
    return assetEyebrow === DEVICE_STATUS.INACTIVE;
  }, [assetEyebrow]);

  const getPCLifecycle = pcManagement?.features.find((item: any) => item.key === 'pcLifecycle');
  const isCheckPCLifecycle = pcManagement?.enabled && Object.keys(getPCLifecycle).length > 0 && getPCLifecycle?.enabled;
  let arrActions = Object.keys(device.actions);
  let textActionsObject = null; 
  if(locale){
    const { textActions } = locale;
    textActionsObject = textActions;
  }
  let arrTextActions = [
    textActionsObject?.deviceRefresh,
    textActionsObject?.deviceReturn,
    textActionsObject?.reportBrokenDevice,
    textActionsObject?.iDontHaveThisDevice,
    textActionsObject?.acknowledgeMyDevice,
    textActionsObject?.reportMissingDevice
  ];

  const PerformanceChip = () => {
    return(
      <HealthyChip
        performanceConfig={performanceConfig}
        performance={performance}
        localeText={localePerformance}
      />
    );
  };
 
  const iconEyeBrow = useMemo(() => {
    return status === DEVICE_STATUS.ELIGIBLE_FOR_REFRESH ? (
      <StyledIcon
        styleIcon={{
          marginTop: '3',
          fontSize: theme.uptimeFontSizes.desktop.fz5 as unknown as string,
          color: isInactiveStatus
            ? theme.uptimeColors.deviceCardEyebrow.deviceCard.color
            : theme.uptimeColors.mainTheme.primary,
        }}
      >
        <Box display="flex">
          <RefreshOutlinedIcon />
        </Box>
      </StyledIcon>
    ) : <></>;
  }, [isInactiveStatus, status, theme]);

  const eyeBrowText = useMemo(() => {
    const isEligibleForRefresh = status === DEVICE_STATUS.ELIGIBLE_FOR_REFRESH;
    const assetEyebrowText: string = !!assetEyebrow
      ? locale?.commonStatus?.[assetEyebrow]
      : locale?.commonStatus?.[DEVICE_STATUS?.IN_USE];

    if (isShowPrimaryLabel && isEligibleForRefresh) {
      return `${locale?.readyForRefresh}${textAssetFunction}`;
    }

    if (isShowPrimaryLabel && !isEligibleForRefresh) {
      return `${assetEyebrowText}${textAssetFunction}`;
    }

    if (!isShowPrimaryLabel && isEligibleForRefresh) {
      return `${locale?.readyForRefresh} - ${assetEyebrowText}`;
    }

    if (!isShowPrimaryLabel && !isEligibleForRefresh) {
      return `${assetEyebrowText}`;
    }

    return '';
  }, [status, assetEyebrow, locale, isShowPrimaryLabel, textAssetFunction]);

  return (
    <Card 
      sx={{
        padding:"16px",
        height:"100%",
        border:"none",
        display: "flex",
        flexDirection:"column",
      }} 
      variant="outlined"
    >
      <Box maxHeight="100dvh" height="100%" overflow="scroll">
        <CardMedia
          component="img"
          image={device.model_picture || GenericDeviceImage}
          alt={device?.display_name}
          sx={{objectFit:"contain",maxHeight:"180px"}}
          title={device?.display_name}
        />

        <CardHeader title={`${(device?.["model.manufacturer"] || device?.["manufacturer"]) || NOT_APPLICABLE} ${device?.model || ''}`} sx={{paddingBottom:"0px"}}/>
        <CardContent sx={{paddingBottom:"8px"}}>
          {/* eyebrow */}
          {assetEyebrow && 
          <EyeBrow
            size="small"
            label={eyeBrowText}
            startIcon={iconEyeBrow}
            variant="filled"
            sx={{
              backgroundColor: isOrange ? theme.uptimeColors.deviceCardEyebrow.deviceCard.background : theme.uptimeColors.deviceCardEyebrow.inUse.background,
              color: isOrange ? theme.uptimeColors.deviceCardEyebrow.deviceCard.color : theme.uptimeColors.deviceCardEyebrow.inUse.color,
              boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "18px",
              letterSpacing: "0.16px",
              textTransform: "uppercase",
            }}
            id="chp-devicedetail-status"
          />}
          {/* info table */}
          {/* #1 */}
          <Grid container sx={{paddingTop:"16px"}} xs={12}>
            <Grid item xs={4}>
              <Box>
                <Typography variant='overline' sx={{color:'text.secondary', lineHeight:'0px'}} gutterBottom={false}>{locale?.serialNumber.toUpperCase()}</Typography>
              </Box>
            </Grid>
            <Grid item xs={8}>
              <Box>
                <Typography variant='body2' sx={{color:'text.secondary'}} gutterBottom={false} id="lbl-devicedetail-serial_number">{device?.serial_number || NOT_APPLICABLE}</Typography>
              </Box>
            </Grid>
          </Grid>
          {/* #2 */}
          {performanceConfig?.isEnabledTachyon &&
            <Grid container sx={{paddingTop:"12px"}} xs={12}>
            <Grid item xs={4}>
              <Box>
                <Typography variant='overline' sx={{color:'text.secondary', lineHeight:'0px'}} gutterBottom={false}>{locale?.performance.toUpperCase()}</Typography>
              </Box>
            </Grid>
            <Grid item xs={8}>
              <Box>
                <PerformanceChip />
              </Box>
            </Grid>
          </Grid>
          }
          {/* #3 */}
          <Grid container sx={{paddingTop:"12px"}} xs={12}>
            <Grid item xs={4}>
              <Box>
                <Typography variant='overline' sx={{color:'text.secondary', lineHeight:'0px'}} gutterBottom={false}>{locale?.lastScanned.toUpperCase()}</Typography>
              </Box>
            </Grid>
            <Grid item xs={8}>
              <Box>
                <Typography variant='body2' sx={{color:'text.secondary'}} gutterBottom={false} id="lbl-devicedetail-last_scanned">{device?.mcpInfo?.last_scanned || device?.configuration?.lastActive || NOT_APPLICABLE}</Typography>
              </Box>
            </Grid>
          </Grid>
          {/* #4 */}
          <Grid container sx={{paddingTop:"12px"}} xs={12}>
            <Grid item xs={4}>
              <Box>
                <Typography variant='overline' sx={{color:'text.secondary', lineHeight:'0px'}} gutterBottom={false}>{locale?.os.toUpperCase()}</Typography>
              </Box>
            </Grid>
            <Grid item xs={8}>
              <Box>
                <Typography variant='body2' sx={{color:'text.secondary'}} gutterBottom={false} id="lbl-devicedetail-os">{device?.os || NOT_APPLICABLE}</Typography>
              </Box>
            </Grid>
          </Grid>
          {/* #5 */}
          {isShowOwnedBy && <Grid container sx={{paddingTop:"12px"}} xs={12}>
            <Grid item xs={4}>
              <Box>
                <Typography variant='overline' sx={{color:'text.secondary', lineHeight:'0px'}} gutterBottom={false}>{locale?.assignedTo.toUpperCase()}</Typography>
              </Box>
            </Grid>
            <Grid item xs={8}>
              <Box>
                <Typography variant='body2' sx={{color:'text.secondary'}} gutterBottom={false} id="lbl-devicedetail-owned_by">{(device?.assignee_name || ownedBy) || NOT_APPLICABLE}</Typography>
              </Box>
            </Grid>
          </Grid>}
          {/* #6 */}
          <Grid container sx={{paddingTop:"12px"}} xs={12}>
            <Grid item xs={4}>
              <Box>
                <Typography variant='overline' sx={{color:'text.secondary', lineHeight:'0px'}} gutterBottom={false}>{locale?.ownedSince.toUpperCase()}</Typography>
              </Box>
            </Grid>
            <Grid item xs={8}>
              <Box>
                <Typography variant='body2' sx={{color:'text.secondary'}} gutterBottom={false} id="lbl-devicedetail-owned_since">{device?.assigned_date || NOT_APPLICABLE}</Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Box>  
      {/* actions buttons */}
      <CardActions disableSpacing sx={{
        flexDirection: "column",
        gap: "10px",
      }}>
        {isCheckPCLifecycle && <>
          {arrActions.map((item, index) => {
              const getKeyAction = getPCLifecycle?.features.find((k: any) => k.key === item);
              return (
                (!!getKeyAction && Object.keys(getKeyAction).length > 0 && getKeyAction?.enabled) &&
                  <React.Fragment key={item}>
                    {arrTextActions[index] ? device.actions[item] ?
                      <StyledButton variant="outlined" onClick={() => onClickAction(item)} size="large" color="primary" fullWidth id={`btn-devicedetail-${mapTextId(item)}`}>{arrTextActions[index]}</StyledButton>
                      :
                      <Tooltip title={locale?.tooltip}>
                        <StyledButton variant="outlined" onClick={() => onClickAction(item)} size="large" color="primary" fullWidth disabled={true} id={`btn-devicedetail-${mapTextId(item)}`}>{arrTextActions[index]}</StyledButton>
                      </Tooltip>
                    : null}
                </React.Fragment>
              );
            })}
        </>}
      </CardActions>
    </Card>
  );
}

export default DeviceDetail;